'use client';

import { useCallback, useState } from 'react';
import { useAppSelector } from 'src/store/client/config';

import { LineArrowDownIcon } from '@/app/_components/icons/line';
import {
  RankingListEmpty,
  RankingListItem,
  RankingListItemSkeleton,
} from '@/app/(football)/[team]/_components/common/RankingListItem';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import type { TopCollectorSeason } from '@/store/server/common/football/football.dto';
import { useGetTopCollectorWithInitQuery } from '@/store/server/common/football/football.query';

const ITEMS_PER_PAGE = 5;

interface TopCollectorItem {
  nickName: string;
  profileImg: string | null;
  cardCnt: number;
  ranking: number;
}

// Simple load more hook
function useLoadMore<T>(data: T[] | undefined, itemsPerPage: number) {
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + itemsPerPage);
  }, [itemsPerPage]);

  const visibleList = data?.slice(0, visibleCount) ?? [];
  const hasMore = data ? visibleCount < data.length : false;

  return { visibleList, hasMore, handleLoadMore };
}

interface RankingSectionProps {
  season: TopCollectorSeason;
}

export default function RankingSection({ season }: RankingSectionProps) {
  const { packName, originalsName, teamSlug, cardName } = useTeamConfig();

  const isLogin = useAppSelector((state) => state.user.LoggedIn);
  const userInfo = useAppSelector((state) => state.user.userInfo);

  const {
    data: topCollectorData,
    isLoading: isLoadingTopCollector,
    isError: isErrorTopCollector,
  } = useGetTopCollectorWithInitQuery({ originalsName, packName, season });

  const { visibleList, hasMore, handleLoadMore } = useLoadMore<TopCollectorItem>(
    topCollectorData?.top ?? undefined,
    ITEMS_PER_PAGE,
  );

  return (
    <section className="flex w-full flex-col items-center gap-8">
      <ul className="flex w-full flex-col gap-3">
        {/* 로딩 중 또는 에러 발생 시 스켈레톤 표시 */}
        {(isLoadingTopCollector || isErrorTopCollector) &&
          Array.from({ length: 10 }).map((_, index) => <RankingListItemSkeleton key={`skeleton-${index}`} />)}

        {/* 데이터 로딩 완료 시 데이터 표시 */}
        {!isLoadingTopCollector && !isErrorTopCollector && (
          <>
            {/* 랭킹 데이터가 없을 시 */}
            {!topCollectorData?.top && (
              <RankingListEmpty
                imageSrc={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_illust_card.webp`}
                imageAlt="탑콜렉터 데이터 없음 아이콘"
              >
                지금 {cardName}를 등록하고
                <br />
                탑콜렉터에 도전하세요!
              </RankingListEmpty>
            )}

            {/* 랭킹 데이터가 있을 시 */}
            {topCollectorData?.top && (
              <>
                {/* 내 랭킹 */}
                {isLogin && (
                  <RankingListItem
                    teamSlug={teamSlug}
                    isMine
                    ranking={topCollectorData?.my?.ranking ?? 0}
                    profileImage={topCollectorData?.my?.profileImg ?? userInfo?.profile_img}
                    nickName={topCollectorData?.my?.nickName ?? userInfo?.nick_name ?? '-'}
                    value={topCollectorData?.my?.cardCnt ?? 0}
                  />
                )}

                {/* 상위 랭킹 */}
                {visibleList.map((item) => (
                  <RankingListItem
                    teamSlug={teamSlug}
                    key={item.ranking}
                    isMine={false}
                    ranking={item.ranking}
                    profileImage={item.profileImg}
                    nickName={item.nickName}
                    value={item.cardCnt}
                  />
                ))}
              </>
            )}
          </>
        )}
      </ul>

      {/* 더보기 버튼 */}
      {hasMore && (
        <button
          onClick={handleLoadMore}
          className="cursor-pointer flex flex-col items-center justify-center gap-[5px] text-base leading-5 font-semibold text-g-00"
        >
          더보기

          <LineArrowDownIcon size={16} color="currentColor" />
        </button>
      )}
    </section>
  );
}
